import { $t } from '@/i18n';
/**
 * 冲突编辑 Hook
 *
 * 提供 Monaco 编辑器中冲突区域的识别、高亮和解决功能
 * 支持 Git 风格的冲突标记（<<<<<<<, =======, >>>>>>>）
 */
import { ref } from '@vue/composition-api';
import { uuidv4 } from '@tencent/ui-core/lib/utils';
import monacoProvider from '@utils/monacoProvider';
import { CONFLICT_MARKER_REGEX, CONFLICT_SPLITTER, AcceptType, } from '../constants';
/**
 * 冲突编辑 Hook
 *
 * @description
 * 处理 Monaco 编辑器中的 Git 风格冲突标记：
 *
 * 1. **冲突识别**: 使用正则表达式搜索 <<<<<<< / ======= / >>>>>>> 标记
 * 2. **区域高亮**: 为当前分支和合并分支的代码块添加不同颜色的背景
 * 3. **操作按钮**: 在每个冲突区域上方显示"接受当前/接受远程/保留两者"按钮
 * 4. **冲突导航**: 支持跳转到上一个/下一个冲突
 *
 * @example
 * ```typescript
 * const { updateConflicts, findMatch, conflictsPosition } = useEditConflict({
 *   editor: editorRef,
 *   classname: {
 *     conflict_current_highlight: styles.current_highlight,
 *     conflict_incoming_highlight: styles.incoming_highlight,
 *   },
 *   currentLabel: '当前分支',
 *   incomingLabel: '合并分支',
 *   onAccept: () => console.log('冲突已解决'),
 * });
 * ```
 *
 * @param options - 配置选项
 * @returns 冲突操作相关的方法和状态
 */
export default function useEditConflict(options) {
    const { editor, classname = {}, currentLabel, incomingLabel, onAccept, } = options;
    // ============================================================================
    // 响应式状态
    // ============================================================================
    /** 编辑器装饰器 ID 列表（用于高亮） */
    const decorations = ref([]);
    /** 当前光标位置（用于冲突导航） */
    const currentPosition = ref({ lineNumber: 1, column: 1 });
    /** 视图区域 ID 列表（用于操作按钮） */
    const widgetZones = ref([]);
    /** 内容 Widget 列表（用于操作按钮） */
    const widgetList = ref([]);
    /** 所有冲突位置信息 */
    const conflictsPosition = ref([]);
    // ============================================================================
    // 冲突识别
    // ============================================================================
    /**
     * 收集编辑器中的所有冲突位置
     *
     * @description
     * 使用正则表达式搜索冲突标记，返回每个冲突的起始行、结束行和分隔符行
     *
     * @returns 冲突位置列表
     */
    const collectConflicts = () => {
        const model = editor.value?.getModel();
        if (!model) {
            return [];
        }
        const conflictsMatch = model.findMatches(CONFLICT_MARKER_REGEX, true, true);
        conflictsPosition.value = conflictsMatch.map((conflictPosition) => {
            const conflictSplitPosition = model.findNextMatch(CONFLICT_SPLITTER, { lineNumber: conflictPosition.range.startLineNumber, column: 1 });
            return {
                startLineNumber: conflictPosition.range.startLineNumber,
                endLineNumber: conflictPosition.range.endLineNumber,
                splitLineNumber: conflictSplitPosition.range.startLineNumber,
            };
        });
        return conflictsPosition.value;
    };
    // ============================================================================
    // 冲突高亮
    // ============================================================================
    /**
     * 高亮冲突区域
     *
     * @description
     * 为每个冲突区域添加背景色装饰器：
     * - 当前分支区域（<<<<<<< 到 =======）使用 current 高亮色
     * - 合并分支区域（======= 到 >>>>>>>）使用 incoming 高亮色
     *
     * @param conflicts - 冲突位置列表
     * @param monaco - Monaco 实例
     */
    const highlightConflicts = (conflicts, monaco) => {
        decorations.value = editor.value?.deltaDecorations(decorations.value, [
            // 当前分支变更高亮（<<<<<<< 到 =======）
            ...conflicts.map(conflict => ({
                range: new monaco.Range(conflict.startLineNumber, 1, conflict.splitLineNumber - 1, 1),
                options: {
                    isWholeLine: true,
                    className: classname.conflict_current_highlight,
                    marginClassName: classname.conflict_current_highlight,
                },
            })),
            // 合并分支变更高亮（======= 到 >>>>>>>）
            ...conflicts.map(conflict => ({
                range: new monaco.Range(conflict.splitLineNumber + 1, 1, conflict.endLineNumber - 1, 1),
                options: {
                    isWholeLine: true,
                    className: classname.conflict_incoming_highlight,
                    marginClassName: classname.conflict_incoming_highlight,
                },
            })),
        ]);
    };
    // ============================================================================
    // 冲突解决
    // ============================================================================
    /**
     * 计算接受特定版本时需要删除的行
     *
     * @param type - 接受类型
     * @param position - 冲突位置
     * @returns 需要删除的行范围列表
     */
    const getRemoveLines = (type, position) => {
        const { startLineNumber, endLineNumber, splitLineNumber } = position;
        switch (type) {
            case AcceptType.CURRENT:
                // 保留当前分支：删除 <<<<<<< 行和 ======= 到 >>>>>>> 的所有行
                return [
                    { startLineNumber, endLineNumber: startLineNumber + 1 },
                    { startLineNumber: splitLineNumber, endLineNumber: endLineNumber + 1 },
                ];
            case AcceptType.INCOMING:
                // 保留合并分支：删除 <<<<<<< 到 ======= 的所有行和 >>>>>>> 行
                return [
                    { startLineNumber, endLineNumber: splitLineNumber + 1 },
                    { startLineNumber: endLineNumber, endLineNumber: endLineNumber + 1 },
                ];
            case AcceptType.BOTH:
                // 保留两者：只删除冲突标记行
                return [
                    { startLineNumber, endLineNumber: startLineNumber + 1 },
                    { startLineNumber: splitLineNumber, endLineNumber: splitLineNumber + 1 },
                    { startLineNumber: endLineNumber, endLineNumber: endLineNumber + 1 },
                ];
            default:
                return [];
        }
    };
    /**
     * 接受指定版本的变更
     *
     * @description
     * 根据用户选择删除对应的代码行，保留选中的版本
     *
     * @param type - 接受类型
     * @param position - 冲突位置
     * @param monaco - Monaco 实例
     */
    const acceptChange = (type, position, monaco) => {
        const removeLines = getRemoveLines(type, position);
        editor.value.executeEdits('', removeLines.map(line => ({
            range: new monaco.Range(line.startLineNumber, 1, line.endLineNumber, 1),
            text: null,
        })));
        onAccept?.();
    };
    // ============================================================================
    // 操作按钮
    // ============================================================================
    /**
     * 清除所有视图区域和 Widget
     */
    const clearAllViewZones = () => {
        editor.value?.changeViewZones((changeAccessor) => {
            widgetZones.value.forEach((zoneId) => {
                changeAccessor.removeZone(zoneId);
            });
        });
        widgetList.value.forEach((widget) => {
            editor.value?.removeContentWidget(widget);
        });
        widgetZones.value = [];
        widgetList.value = [];
    };
    /**
     * 添加冲突操作按钮
     *
     * @description
     * 在每个冲突区域上方显示操作按钮：
     * - 使用当前版本
     * - 使用远程版本
     * - 保留两个版本
     *
     * @param conflicts - 冲突位置列表
     * @param monaco - Monaco 实例
     */
    const addAcceptWidgets = (conflicts, monaco) => {
        clearAllViewZones();
        conflicts.forEach((conflictPosition) => {
            // 创建按钮容器
            const domNode = document.createElement('div');
            domNode.style.color = 'gray';
            domNode.style.fontSize = '12px';
            domNode.style.whiteSpace = 'nowrap';
            // 定义按钮配置
            const buttons = [
                {
                    label: currentLabel ? $t('k_216c1998', { arg_0: currentLabel }) : $t('k_a0e0f1d7'),
                    type: AcceptType.CURRENT,
                },
                {
                    label: incomingLabel ? $t('k_216c1998', { arg_0: incomingLabel }) : $t('k_79f7a837'),
                    type: AcceptType.INCOMING,
                },
                {
                    label: $t('k_2926ff7'),
                    type: AcceptType.BOTH,
                },
            ];
            // 创建按钮元素
            buttons.forEach((button) => {
                const element = document.createElement('span');
                element.style.cursor = 'pointer';
                element.style.marginRight = '4px';
                element.innerText = button.label;
                element.addEventListener('click', () => acceptChange(button.type, conflictPosition, monaco));
                domNode.appendChild(element);
            });
            // 添加视图区域（为按钮腾出空间）
            editor.value.changeViewZones((changeAccessor) => {
                const viewZoneId = changeAccessor.addZone({
                    afterLineNumber: conflictPosition.startLineNumber - 1,
                    heightInLines: 1,
                    domNode: document.createElement('div'),
                });
                widgetZones.value.push(viewZoneId);
            });
            // 添加内容 Widget（显示按钮）
            const id = uuidv4();
            const widget = {
                domNode: null,
                getDomNode: () => domNode,
                getId: () => id,
                getPosition() {
                    return {
                        position: {
                            lineNumber: conflictPosition.startLineNumber,
                            column: 1,
                        },
                        preference: [
                            monaco.editor.ContentWidgetPositionPreference.ABOVE,
                            monaco.editor.ContentWidgetPositionPreference.BELOW,
                        ],
                    };
                },
            };
            widgetList.value.push(widget);
            editor.value?.addContentWidget(widget);
        });
    };
    // ============================================================================
    // 公开方法
    // ============================================================================
    /**
     * 更新冲突状态
     *
     * @description
     * 重新扫描编辑器内容，更新冲突高亮和操作按钮
     *
     * @param autoFocus - 是否自动跳转到第一个冲突
     */
    const updateConflicts = (autoFocus) => {
        monacoProvider((monaco) => {
            if (!editor.value)
                return;
            // 收集冲突位置
            const conflicts = collectConflicts();
            // 更新高亮
            highlightConflicts(conflicts, monaco);
            // 更新操作按钮
            addAcceptWidgets(conflicts, monaco);
            // 自动跳转到第一个冲突
            if (autoFocus && conflicts.length > 0) {
                currentPosition.value = {
                    lineNumber: conflicts[0].endLineNumber,
                    column: 1,
                };
                editor.value.revealPositionInCenter(currentPosition.value);
            }
        });
    };
    /**
     * 跳转到上一个或下一个冲突
     *
     * @param direction - 跳转方向：'Next' 或 'Previous'
     */
    const findMatch = (direction) => {
        monacoProvider(() => {
            const model = editor.value?.getModel();
            if (!model)
                return;
            const conflictsMatch = model[`find${direction}Match`](CONFLICT_MARKER_REGEX, currentPosition.value, true, true);
            if (conflictsMatch) {
                editor.value?.revealPositionInCenter({
                    lineNumber: conflictsMatch.range.startLineNumber,
                    column: 1,
                });
                currentPosition.value = {
                    column: 1,
                    lineNumber: conflictsMatch.range.endLineNumber,
                };
            }
        });
    };
    return {
        /** 更新冲突状态（高亮、按钮等） */
        updateConflicts,
        /** 跳转到上一个/下一个冲突 */
        findMatch,
        /** 当前所有冲突的位置信息 */
        conflictsPosition,
    };
}
